import {
  type ClipboardEvent,
  type ReactElement,
  useMemo,
  useRef,
  type ComponentProps,
} from 'react';
import {
  Bold,
  Code,
  Heading2,
  Image,
  Italic,
  Link,
  List,
  ListOrdered,
  Quote,
  Strikethrough,
  Table2,
} from 'lucide-react';
import { Button, IconButton, MarkdownBody, TextField, Tooltip } from '@/components';
import { cn } from '@/lib/cn';
import { useInterfaceLanguage } from '@/lib/interface-language';
import {
  applyRichTextPaste,
  formatMarkdown,
  markdownFormatFallbacks,
  type MarkdownFormatMode,
} from '@/lib/markdown-editing';

export interface RichDescriptionEditorProps {
  value: string;
  maxLength: number;
  warnAt: number;
  error?: string | undefined;
  busy?: boolean | undefined;
  onChange: (value: string) => void;
  onSave: () => void;
  onCancel: () => void;
}

export function cardDescriptionDraftKey(
  cardId: string,
  currentUserId: string | undefined,
): string {
  return `molt:card-description-draft:${currentUserId ?? 'anonymous'}:${cardId}`;
}

/**
 * FE-80.1 — each entry carries a catalogue KEY, not a label.
 *
 * ⚠ `labelKey` is read TWICE per entry in the render below — once as the
 * `Tooltip`'s `content` and once as the `IconButton`'s `label` — so twelve
 * entries occupy twenty-four sinks. Both reads come from ONE `t(tool.labelKey)`
 * binding on purpose: two `t` calls could drift to two keys, and the tooltip
 * sink is invisible to a default render (Radix mounts `Tooltip` content only
 * while open), so a drift there would not reach the leak scan.
 *
 * ⚠ The `format.` segment in every key is deliberate — see the catalogue block
 * for `cardBack.description.editor.*`. Dropping it from the first entry yields
 * `cardBack.description.editor.heading`, one segment from the EXISTING
 * `cardBack.description.heading`, whose value is `Description`.
 */
const TOOLBAR: readonly {
  mode: MarkdownFormatMode;
  labelKey: string;
  icon: ReactElement;
}[] = [
  {
    mode: 'heading',
    labelKey: 'cardBack.description.editor.format.heading',
    icon: <Heading2 />,
  },
  { mode: 'bold', labelKey: 'cardBack.description.editor.format.bold', icon: <Bold /> },
  {
    mode: 'italic',
    labelKey: 'cardBack.description.editor.format.italic',
    icon: <Italic />,
  },
  {
    mode: 'strike',
    labelKey: 'cardBack.description.editor.format.strikethrough',
    icon: <Strikethrough />,
  },
  {
    mode: 'code',
    labelKey: 'cardBack.description.editor.format.inlineCode',
    icon: <Code />,
  },
  { mode: 'link', labelKey: 'cardBack.description.editor.format.link', icon: <Link /> },
  {
    mode: 'bullet',
    labelKey: 'cardBack.description.editor.format.bulletList',
    icon: <List />,
  },
  {
    mode: 'number',
    labelKey: 'cardBack.description.editor.format.numberedList',
    icon: <ListOrdered />,
  },
  {
    mode: 'quote',
    labelKey: 'cardBack.description.editor.format.quote',
    icon: <Quote />,
  },
  {
    mode: 'codeBlock',
    labelKey: 'cardBack.description.editor.format.codeBlock',
    icon: <Code />,
  },
  {
    mode: 'image',
    labelKey: 'cardBack.description.editor.format.image',
    icon: <Image />,
  },
  {
    mode: 'table',
    labelKey: 'cardBack.description.editor.format.table',
    icon: <Table2 />,
  },
];

export function RichDescriptionEditor({
  value,
  maxLength,
  warnAt,
  error,
  busy,
  onChange,
  onSave,
  onCancel,
}: RichDescriptionEditorProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const markdownFallbacks = useMemo(() => markdownFormatFallbacks(t), [t]);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const errorProps =
    error === undefined
      ? {}
      : ({ error } satisfies Partial<Pick<ComponentProps<typeof TextField>, 'error'>>);
  const busyProps = busy === undefined ? {} : { busy };

  function update(next: string) {
    onChange(next.slice(0, maxLength));
  }

  function applyFormat(mode: MarkdownFormatMode) {
    const textarea = textareaRef.current;
    const start = textarea?.selectionStart ?? value.length;
    const end = textarea?.selectionEnd ?? value.length;
    const selected = value.slice(start, end);
    const next = formatMarkdown(markdownFallbacks, value, start, end, selected, mode);
    update(next.value);

    window.setTimeout(() => {
      textarea?.focus();
      textarea?.setSelectionRange(
        Math.min(next.selectionStart, maxLength),
        Math.min(next.selectionEnd, maxLength),
      );
    }, 0);
  }

  function handlePaste(event: ClipboardEvent<HTMLTextAreaElement>) {
    const html = event.clipboardData.getData('text/html');
    if (html.trim() === '') return;
    event.preventDefault();

    const textarea = textareaRef.current;
    const start = textarea?.selectionStart ?? value.length;
    const end = textarea?.selectionEnd ?? value.length;
    const result = applyRichTextPaste({
      value,
      html,
      selectionStart: start,
      selectionEnd: end,
      variant: 'description',
      maxLength,
    });
    update(result.value);
    window.setTimeout(() => {
      textarea?.focus();
      textarea?.setSelectionRange(result.selectionStart, result.selectionEnd);
    }, 0);
  }

  return (
    <div className="flex flex-col gap-3">
      <div
        aria-label={t('cardBack.description.editor.toolbar')}
        className="flex flex-wrap items-center gap-1 rounded-card border border-border bg-surface-raised p-1"
        role="toolbar"
      >
        {TOOLBAR.map((tool) => {
          // ⚠ ONE binding for BOTH sinks. See the `TOOLBAR` docblock.
          const label = t(tool.labelKey);
          return (
            <Tooltip key={tool.mode} content={label}>
              <IconButton
                type="button"
                size="sm"
                label={label}
                onClick={() => applyFormat(tool.mode)}
              >
                {tool.icon}
              </IconButton>
            </Tooltip>
          );
        })}
      </div>

      <TextField
        ref={textareaRef}
        label={t('cardBack.description.editor.fieldLabel')}
        multiline
        value={value}
        onPaste={handlePaste}
        onChange={(event) => update(event.currentTarget.value)}
        counter={{ value: value.length, max: maxLength, warnAt }}
        {...errorProps}
      />

      <section
        aria-label={t('cardBack.description.editor.preview')}
        className={cn(
          'min-h-20 rounded-card border border-border bg-surface p-3',
          value.trim() === '' && 'flex items-center',
        )}
      >
        {value.trim() === '' ? (
          <p className="text-sm text-ink-muted">
            {t('cardBack.description.editor.previewEmpty')}
          </p>
        ) : (
          <MarkdownBody markdown={value} />
        )}
      </section>

      <div className="flex flex-wrap gap-2">
        {/*
          ⚠ `busyLabel` fills a slot this file never filled. Without it the Save
          button announces `Button`'s UNROUTED default `Working…` into its
          sr-only `role="status"` whenever `busy` is true — the one English leak
          on this surface that no literal in this file authored. Passing a value
          here leaves `Button.tsx` untouched, so fe-plan residual 9 survives for
          whoever funds the primitive.
        */}
        <Button
          size="sm"
          variant="primary"
          onClick={onSave}
          busyLabel={t('cardBack.description.editor.saving')}
          {...busyProps}
        >
          {t('cardBack.description.editor.save')}
        </Button>
        <Button size="sm" variant="quiet" onClick={onCancel}>
          {t('cardBack.description.editor.cancel')}
        </Button>
      </div>
    </div>
  );
}
